/**
 * @fileoverview Zod schema for My Page's "Update stage" form.
 * @description The new stage, plus a reason of 1-500 characters that Lost, On Hold and Rejected
 * require. Messages are translation keys, translated only at the display call site in
 * MyPageStageDialog.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { z } from 'zod'

import {
  MY_PAGE_REASON_MAX_LENGTH,
  MY_PAGE_REASON_STAGES,
} from '@/features/sales/constants/my-page.constants'

import type { MyPageStageCode } from '@/features/sales/types/my-page.types'

/** @description The stage codes the form can hold (an empty string until one is chosen). */
const STAGE_VALUES = [
  '',
  'CONTACTED',
  'DEMO_REQUESTED',
  'PROPOSAL_SENT',
  'UNDER_NEGOTIATION',
  'SUBSCRIPTION_REQUESTED',
  'ON_HOLD',
  'LOST',
  'REJECTED',
] as const

/** @description The update-stage form's rules. */
export const myPageStageFormSchema = z
  .object({
    toStage: z.enum(STAGE_VALUES),
    reason: z
      .string()
      .trim()
      .max(MY_PAGE_REASON_MAX_LENGTH, 'sales.myWork.dialogs.errors.reasonTooLong'),
  })
  .refine((values) => values.toStage !== '', {
    message: 'sales.myWork.dialogs.stage.errors.stageRequired',
    path: ['toStage'],
  })
  .refine(
    (values) =>
      !MY_PAGE_REASON_STAGES.includes(values.toStage as MyPageStageCode) || values.reason !== '',
    { message: 'sales.myWork.dialogs.errors.reasonRequired', path: ['reason'] },
  )

/** @description The update-stage form's values. */
export type MyPageStageFormValues = z.infer<typeof myPageStageFormSchema>
